import checkingIcon from "@assets/DoorUnlockStatus/checking.svg";
import failedIcon from "@assets/DoorUnlockStatus/failed.svg";
import offlineIcon from "@assets/DoorUnlockStatus/offline.svg";
import reconnectingIcon from "@assets/DoorUnlockStatus/reconnecting.svg";
import { IllustrationOpeningAnimation } from "@assets/IllustrationOpeningAnimation";
import { useI18nContext } from "@i18n/i18n-react";
import { Button } from "@shadcn/ui/button";
import { Loader2 } from "lucide-react";

export type DoorUnlockInterruption = "checking" | "failed" | "offline" | "reconnecting";

type Props = {
	state: DoorUnlockInterruption;
	onExit?: () => void;
	onRetry: () => void;
};

export function DoorUnlockStatusPanel(props: Props) {
	const { state, onExit, onRetry } = props;
	const LL = useI18nContext().LL.shared;
	const content = {
		checking: {
			title: LL.DOOR_STATUS_CHECKING_TITLE(),
			description: LL.DOOR_STATUS_CHECKING_DESCRIPTION(),
			icon: checkingIcon,
		},
		failed: {
			title: LL.DOOR_STATUS_FAILED_TITLE(),
			description: LL.DOOR_STATUS_FAILED_DESCRIPTION(),
			icon: failedIcon,
		},
		offline: {
			title: LL.DOOR_STATUS_OFFLINE_TITLE(),
			description: LL.DOOR_STATUS_OFFLINE_DESCRIPTION(),
			icon: offlineIcon,
		},
		reconnecting: {
			title: LL.DOOR_STATUS_RECONNECTING_TITLE(),
			description: LL.DOOR_STATUS_RECONNECTING_DESCRIPTION(),
			icon: reconnectingIcon,
		},
	}[state];

	return (
		<section className="mx-auto flex w-full max-w-sm flex-col gap-4" aria-live="polite" data-testid="door-unlock-status" data-state={state}>
			<div className="space-y-4">
				<h2 className="text-2xl font-breit">{content.title}</h2>
				<p className="text-sm whitespace-pre-line text-muted-foreground">{content.description}</p>
			</div>
			<div className="relative flex min-h-64 items-center justify-center overflow-hidden">
				<IllustrationOpeningAnimation className="w-full max-w-[342px]" />
				<img src={content.icon} alt="" className="absolute size-20" />
			</div>
			{state === "offline" || state === "checking" ? (
				<div className="flex items-center gap-2 rounded-lg border bg-background p-4 text-sm font-medium shadow-md" role="status">
					<Loader2 className="size-5 shrink-0 animate-spin" />
					{state === "offline" ? LL.DOOR_STATUS_WAITING_FOR_CONNECTION() : LL.DOOR_STATUS_CHECKING_PROGRESS()}
				</div>
			) : null}
			{state === "failed" ? (
				<Button variant="outline" size="lg" onClick={onRetry}>
					{LL.DOOR_STATUS_CHECK_AGAIN()}
				</Button>
			) : null}
			{state === "reconnecting" && onExit ? (
				<Button size="lg" onClick={onExit}>
					{LL.DOOR_STATUS_LEAVE()}
				</Button>
			) : null}
		</section>
	);
}
